/**
 * Tipos compartidos para la migración v3 del prompt de estimación.
 * Catálogo cerrado de warning codes, source subtypes, confidence, etc.
 * Ver MIGRATION-GUIDE-v2-to-v3.md para más detalle.
 */

// ============================================================================
// UNION LITERALS
// ============================================================================

/** Origen de una tarea. Mantiene compatibilidad con valores actuales (con/sin tilde). */
export type EstimationSource =
  | 'IA'
  | 'Histórica'
  | 'Historica'
  | 'IA (Modificada)'
  | 'Histórica (Modificada)'
  | 'Manual';

/** Subtipo de origen v3 — trazabilidad fina. */
export type EstimationSourceSubtype =
  | 'industry_baseline'    // IA — estándar sectorial
  | 'inferred_gap'         // IA — vacío técnico inferido
  | 'sdlc_standard'        // IA — fases estándar SDLC
  | 'estimated_history'    // Histórica — reutilizada y calibrada
  | 'execution_history'    // Histórica — heredada de ejecución con match
  | 'execution_only';      // Histórica — fantasma (solo en execution)

export type EstimationConfidence = 'LOW' | 'MEDIUM' | 'HIGH';

export type EstimationProjectType =
  | 'NEW' | 'EVOLUTIONARY' | 'RECURRING' | 'MAINTENANCE' | 'UNKNOWN';

export type EstimationBasis =
  | 'industry_baseline_2026' | 'historical_calibrated' | 'mixed';

export type EstimationAILevel = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';

/** Catálogo cerrado de códigos de warning. */
export type EstimationWarningCode =
  | 'PROMPT_INJECTION_BLOCKED'
  | 'PII_REDACTED'
  | 'SCOPE_INSUFFICIENT'
  | 'OUT_OF_DOMAIN'
  | 'LOW_HISTORICAL_SAMPLE'
  | 'STALE_HISTORICAL_CONTEXT'
  | 'LOW_SIMILARITY_CONTEXT'
  | 'ASSUMED_TECHNOLOGY'
  | 'ASSUMED_PROFILE'
  | 'LARGE_SCOPE_BUDGET_RISK'
  | 'DEADLINE_RISK'
  | 'INFERRED_GAPS_ADDED'
  | 'PHANTOM_TASKS_ADDED'
  | 'ROLLUP_RECALCULATED'
  | 'OTHER';

export type EstimationDiffStatus = 'unchanged' | 'added' | 'removed' | 'modified';

// ============================================================================
// INTERFACES
// ============================================================================

export interface CanonicalSignature {
  category: string;
  core_actions: string[];
  core_objects: string[];
}

export interface EstimationWarning {
  code: EstimationWarningCode;
  details?: string;
}

export interface EstimationSelfCheck {
  sdlc_present?: boolean;
  deduplication_applied?: boolean;
  guardrails_respected?: boolean;
  calibration_applied_when_data?: boolean;
  rollup_consistent?: boolean;
  all_tasks_have_signature?: boolean;
}

export interface EstimationCalibrationSummary {
  samples_used?: number;
  median_calibration_ratio?: number;
  phantom_tasks_detected?: number;
  outliers_excluded?: number;
}

export interface EstimationMetadata {
  prompt_version?: string;
  project_type_classified?: EstimationProjectType;
  estimation_basis?: EstimationBasis;
  ai_assistance_level_assumed?: EstimationAILevel;
  ai_assistance_observed_factor?: number;
  overall_confidence?: EstimationConfidence;
  calibration_summary?: EstimationCalibrationSummary;
  warnings?: EstimationWarning[];
  self_check?: EstimationSelfCheck;
  reasoning_trace?: string;
}

export interface EstimationRollup {
  total_p50_hours?: number;
  contingency_buffer_hours?: number;
  total_with_contingency_hours?: number;
  phantom_tasks_total_hours?: number;
}

// ============================================================================
// CONSTANTES DE UI
// ============================================================================

/** Códigos de warning que bloquean la estimación (no hay tareas reales). */
export const BLOCKING_WARNING_CODES: ReadonlyArray<EstimationWarningCode> = [
  'PROMPT_INJECTION_BLOCKED',
  'OUT_OF_DOMAIN',
  'SCOPE_INSUFFICIENT'
];

/** Permiso granular para ver el panel de diagnóstico (drawer admin). */
export const PERMISSION_VIEW_DIAGNOSTICS = 'estimation:viewDiagnostics';

// ============================================================================
// HELPERS PUROS
// ============================================================================

export function isBlockingWarningCode(code: EstimationWarningCode): boolean {
  return BLOCKING_WARNING_CODES.includes(code);
}